/**
 * Everything the calendar knows about a program, in one block.
 *
 * These facts used to be scattered across three places that could disagree: pills built from
 * the program's columns, an amber "No class on" panel derived from the closure calendar, and
 * whatever the vendor had typed into the description months earlier. A parent reading the
 * Basketball page saw "11 sessions in total" twice — once as prose the admin maintained by
 * hand and once as a computed figure — and had no way to know which was current.
 *
 * So the schedule, the term, the billing model, the session count and the days off are now a
 * single card rendered from the program's own fields. The description is free to be a
 * description again, and an admin editing dates changes one thing rather than three.
 *
 * Shared by the program page and the enrollment page deliberately: a family looking at a
 * class they have already bought asks exactly the same questions as one deciding whether to.
 */

import { ReactNode } from 'react';
import { CalendarRange, Clock } from 'lucide-react';
import { Program, getScheduleTimeRange } from '../types/program';
import { formatSessionTimeRange } from '../utils/schedule';
import { sessionBreakdown, parentClosureLabel } from '../utils/sessionBreakdown';
import { isNonTeachingProgramType, isDaySelectionBilling } from '../utils/programCalendar';
import { getBillingTypeDisplay } from '../utils/billingDisplay';
import { formatDateDisplay } from '../utils/date';
import { useLabels, useBillingTypeLabel } from '../config/useLabels';

/** The slice of a program this reads. Kept narrow so callers can pass a partial select. */
export type ScheduleSummaryProgram = Pick<
  Program,
  'type' | 'billing_type' | 'schedule_cron' | 'start_date' | 'end_date'
> &
  Partial<Pick<Program, 'duration' | 'excluded_dates' | 'brand_key'>>;

interface ProgramScheduleSummaryProps {
  program: ScheduleSummaryProgram;
  /**
   * The day this family's term starts, when it is not the program's own start date. The
   * enrollment page passes `enrollment_start_date` so the range, the session count and the
   * days off all describe the term the family actually bought rather than the one on offer.
   */
  termStart?: string | null;
  /** Pills specific to one surface — a payment due date, a lunch selection. */
  extraPills?: ReactNode;
  className?: string;
}

function Pill({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1 text-xs bg-white border border-gray-200 rounded-full px-2 py-0.5 text-gray-700">
      {children}
    </span>
  );
}

export function ProgramScheduleSummary({
  program,
  termStart,
  extraPills,
  className = '',
}: ProgramScheduleSummaryProps) {
  const t = useLabels();
  const billingLabel = useBillingTypeLabel();

  // A waitlist or registration fee has no timetable: its cron is a placeholder written to
  // satisfy the column, and reading it as a schedule claims a 9am class every day of the week.
  const meets = !isNonTeachingProgramType(program.type);
  const { Icon: BillingIcon } = getBillingTypeDisplay(program.billing_type);

  const startDate = termStart || program.start_date;
  const timeRange = meets ? formatSessionTimeRange(program.schedule_cron, program.duration) : null;

  let breakdown;
  try {
    breakdown = meets
      ? sessionBreakdown({
          type: program.type,
          schedule_cron: program.schedule_cron,
          start_date: startDate,
          end_date: program.end_date,
          excluded_dates: program.excluded_dates,
          // Load-bearing. `sessionBreakdown` defaults a missing brand to BHCS, so omitting it
          // here printed the school district's holidays onto a Kaleido program AND subtracted
          // them from its session count -- a wrong number, not just a wrong label.
          brand_key: program.brand_key,
        })
      : null;
  } catch {
    // An unreadable cron is not worth white-screening a page a parent is trying to enrol from.
    breakdown = null;
  }

  // No total for a plan-driven program. The cron is a menu of days a family may pick, not a
  // course they are buying, so "205 sessions" on the Flex page described something nobody
  // attends and sat next to a price.
  const sessionCount =
    breakdown && !isDaySelectionBilling(program.billing_type) ? breakdown.sessionCount : null;
  const skipped = breakdown?.skipped ?? [];

  return (
    <section className={`rounded-lg border border-gray-200 bg-gray-50 p-4 ${className}`.trim()}>
      <h3 className="text-sm font-semibold text-gray-900 mb-3">{t('header.programDetails')}</h3>

      <div className="flex flex-wrap gap-2">
        {meets && <Pill>{getScheduleTimeRange(program.schedule_cron)}</Pill>}
        {timeRange && (
          <Pill>
            <Clock className="w-3 h-3" />
            {timeRange}
          </Pill>
        )}
        <Pill>
          <CalendarRange className="w-3 h-3" />
          {formatDateDisplay(startDate)} – {formatDateDisplay(program.end_date)}
        </Pill>
        {sessionCount !== null && (
          <Pill>
            {sessionCount} {sessionCount === 1 ? t('label.session') : t('label.sessions')}
          </Pill>
        )}
        <Pill>
          <BillingIcon className="w-3 h-3" />
          {billingLabel(program.billing_type)}
        </Pill>
        {extraPills}
      </div>

      {skipped.length > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          {/*
            One chronological list rather than the two it used to be. Splitting school closures
            from the program's own days off put 10/02 below 11/27, and a parent scanning for
            "is there class next Friday" reads a calendar, not a taxonomy. The distinction is
            kept where it belongs — in each row's reason.
          */}
          <h4 className="text-xs font-semibold uppercase tracking-wide text-amber-800 mb-1.5">
            {t('label.noClassOn')}
          </h4>
          <ul className="space-y-1">
            {skipped.map(sk => {
              const reason =
                sk.source === 'program'
                  ? t('label.programDayOff')
                  : parentClosureLabel(sk.reason);
              return (
                <li key={sk.date} className="text-sm text-gray-700">
                  <span className="font-medium text-gray-900">{formatDateDisplay(sk.date)}</span>
                  {reason && <span className="text-gray-600"> &mdash; {reason}</span>}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </section>
  );
}
